import { ApiDiffResponseDto, ApiComparisonModel, ComparisonEntry, ComparisonSection } from '../types/apiDiff';

/**
 * Normalizes the raw backend diff response into a UI-agnostic comparison model.
 * Keeping this transformation isolated lets rendering components change freely
 * without depending on the backend DTO shape (per MSIAPI-1285 technical notes).
 */
export function normalizeApiDiff(dto: ApiDiffResponseDto): ApiComparisonModel {
    const metadataEntries: ComparisonEntry[] = (dto.diff?.metadata?.changed || []).map((field) => ({
        id: `metadata-${field.field}`,
        status: 'changed',
        title: field.field,
        from: field.from,
        to: field.to,
    }));

    const endpointEntries: ComparisonEntry[] = [
        ...(dto.diff?.endpoints?.added || []).map((e) => ({
            id: `endpoint-added-${e.method}-${e.path}`,
            status: 'added' as const,
            title: `${e.method} ${e.path}`,
            detail: e.summary,
        })),
        ...(dto.diff?.endpoints?.changed || []).map((e) => ({
            id: `endpoint-changed-${e.method}-${e.path}`,
            status: 'changed' as const,
            title: `${e.method} ${e.path}`,
            changes: e.changes,
        })),
        ...(dto.diff?.endpoints?.deprecated || []).map((e: any, index: number) => ({
            id: `endpoint-deprecated-${e.method || 'unknown'}-${e.path || index}`,
            status: 'deprecated' as const,
            title: `${e.method || ''} ${e.path || ''}`.trim() || `Deprecated endpoint ${index + 1}`,
            detail: e.summary,
        })),
        ...(dto.diff?.endpoints?.removed || []).map((e) => ({
            id: `endpoint-removed-${e.method}-${e.path}`,
            status: 'removed' as const,
            title: `${e.method} ${e.path}`,
        })),
    ];

    const schemaEntries: ComparisonEntry[] = [
        ...(dto.diff?.schemas?.added || []).map((name) => ({
            id: `schema-added-${name}`,
            status: 'added' as const,
            title: name,
        })),
        ...(dto.diff?.schemas?.changed || []).map((s) => ({
            id: `schema-changed-${s.schema}`,
            status: 'changed' as const,
            title: s.schema,
            changes: s.changes,
        })),
        ...(dto.diff?.schemas?.removed || []).map((name) => ({
            id: `schema-removed-${name}`,
            status: 'removed' as const,
            title: name,
        })),
    ];

    const sections: ComparisonSection[] = [
        { key: 'metadata', label: 'Metadata', entries: metadataEntries },
        { key: 'endpoints', label: 'Endpoints', entries: endpointEntries },
        { key: 'schemas', label: 'Schemas', entries: schemaEntries },
    ];

    const totalChangeCount = sections.reduce((sum, section) => sum + section.entries.length, 0);

    return {
        apiId: dto.comparisonMetadata?.apiId,
        fromVersion: dto.comparisonMetadata?.fromVersion,
        toVersion: dto.comparisonMetadata?.toVersion,
        sections,
        totalChangeCount,
    };
}
